(() => {
  if (window.CedarAmberStudio) { window.CedarAmberStudio.init(); return; }
  const instances = new Map();
  const modules = new Map();
  let activeStudio;
  let creationQueue = Promise.resolve();
  const loadRenderer = url => {
    if (!modules.has(url)) modules.set(url, import(url).catch(error => { modules.delete(url); throw error; }));
    return modules.get(url);
  };

  function mount(root) {
    if (instances.has(root)) return;
    const life = new AbortController();
    const signal = life.signal;
    const $ = selector => root.querySelector(selector);
    const $$ = selector => [...root.querySelectorAll(selector)];
    const on = (target, event, handler, options = {}) => target?.addEventListener(event, handler, { ...options, signal });
    const productRoot = root.closest('[data-ca-product]');
    const stage = $('[data-ca-studio-stage]');
    const poster = $('[data-ca-studio-poster]');
    const launcher = $('[data-ca-studio-launch]');
    const launchLabel = $('[data-ca-studio-launch-label]');
    const status = $('[data-ca-studio-status]');
    const controls = $('[data-ca-studio-controls]');
    const exitButton = $('[data-ca-studio-exit]');
    const help = $('[data-ca-studio-help]');
    let canvas = $('[data-ca-studio-canvas]');
    let strings = {};
    try { strings = JSON.parse($('[data-ca-studio-strings]')?.textContent || '{}'); } catch {}
    let viewer;
    let capabilities;
    let revision = 0;
    let visible = stage.getBoundingClientRect().bottom > 0 && stage.getBoundingClientRect().top < innerHeight;
    let view = 'exterior';
    let lighting = 'dusk';
    let door = 0;
    let freshCanvasNeeded = false;
    const owner = { release: () => release(true) };

    function state(value, message = '') {
      root.dataset.state = value;
      const ready = value === 'ready';
      const loading = value === 'loading';
      stage.setAttribute('aria-busy', String(loading));
      canvas.hidden = !ready && !loading;
      canvas.tabIndex = ready ? 0 : -1;
      poster.setAttribute('aria-hidden', String(ready));
      poster.inert = ready;
      controls.hidden = !ready;
      exitButton.hidden = !ready;
      launcher.disabled = loading;
      launcher.setAttribute('aria-busy', String(loading));
      launchLabel.textContent = loading ? strings.loading : value === 'error' ? strings.retry : strings.launch;
      status.textContent = message;
      help.textContent = ready ? strings.helpReady : strings.helpIdle;
    }

    function syncNative() {
      const price = productRoot?.querySelector('[data-ca-price]');
      const availability = productRoot?.querySelector('[data-ca-availability]');
      if (price) $('[data-ca-studio-price]').textContent = price.textContent.trim();
      if (availability) $('[data-ca-studio-availability]').textContent = availability.textContent.replace(/\s+/g, ' ').trim();
    }

    function syncOptions() {
      $$('[data-ca-studio-view]').forEach(button => {
        const supported = !capabilities || capabilities.views?.includes(button.dataset.caStudioView);
        button.hidden = !supported;
        button.setAttribute('aria-selected', String(button.dataset.caStudioView === view));
        button.tabIndex = button.dataset.caStudioView === view ? 0 : -1;
      });
      $$('[data-ca-studio-panel]').forEach(panel => { panel.hidden = panel.dataset.caStudioPanel !== view; });
      $$('[data-ca-studio-light]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.caStudioLight === lighting)));
      $('[data-ca-studio-light-controls]').hidden = !!capabilities && !capabilities.lighting;
      const doorButton = $('[data-ca-studio-door]');
      doorButton.hidden = !!capabilities && !capabilities.door;
      doorButton.setAttribute('aria-pressed', String(door > 0));
      $('[data-ca-studio-door-label]').textContent = door > 0 ? strings.closeDoor : strings.openDoor;
      $('[data-ca-studio-reset]').hidden = !!viewer && typeof viewer.resetView !== 'function';
      root.dataset.view = view;
    }

    function setView(value) {
      if (!['exterior', 'interior', 'detail', 'plan'].includes(value)) return;
      if (capabilities && !capabilities.views?.includes(value)) return;
      view = value;
      if (viewer) {
        viewer.setView(view);
        if (view === 'interior' && capabilities?.door) { door = 1; viewer.setDoor(door); }
      }
      syncOptions();
    }

    function visibility() {
      const running = visible && !document.hidden;
      viewer?.setInteractive(running);
      viewer?.setMotionEnabled(running);
      if (running) viewer?.resize();
    }

    function release(update = false) {
      revision += 1;
      if (viewer) { viewer.dispose(); viewer = null; freshCanvasNeeded = true; }
      if (activeStudio === owner) activeStudio = null;
      capabilities = undefined;
      if (update && !signal.aborted) { state('idle'); syncOptions(); }
    }

    async function launch() {
      if (root.dataset.state === 'loading' || root.dataset.state === 'ready' || signal.aborted) return;
      if (activeStudio && activeStudio !== owner) activeStudio.release();
      activeStudio = owner;
      const attempt = ++revision;
      const current = () => attempt === revision && !signal.aborted && root.isConnected;
      if (freshCanvasNeeded) {
        const replacement = canvas.cloneNode(true);
        canvas.replaceWith(replacement);
        canvas = replacement;
        freshCanvasNeeded = false;
      }
      state('loading', strings.loading || 'Opening the 3D studio…');
      let readyMetadata;
      const fail = () => {
        if (!current()) return;
        release(false);
        freshCanvasNeeded = true;
        state('error', strings.error || 'The 3D view could not open. Product photographs remain available.');
      };
      const ready = metadata => {
        if (!current()) return;
        readyMetadata = metadata;
        if (!viewer) return;
        capabilities = viewer.getState()?.capabilities || metadata?.capabilities || { views: ['exterior'], door: false, lighting: false };
        if (!capabilities.views?.includes(view)) view = capabilities.views?.[0] || 'exterior';
        viewer.setLighting(lighting);
        viewer.setView(view);
        if (capabilities.door) { if (view === 'interior') door = 1; viewer.setDoor(door); }
        const shouldFocus = document.activeElement === launcher;
        state('ready', strings.ready || 'The interactive design view is ready.');
        syncOptions();
        visibility();
        if (shouldFocus) canvas.focus({ preventScroll: true });
      };
      try {
        // Module and Three.js are requested only in response to the launch button.
        const module = await loadRenderer(root.dataset.viewerUrl);
        if (!current()) return;
        if (typeof module.createProductViewer !== 'function') throw new Error('Viewer module unavailable');
        // Theme-editor replacements can overlap asynchronous setup. Serialize creation
        // so a stale setup is disposed before another WebGL context is created.
        creationQueue = creationQueue.catch(() => {}).then(async () => {
          if (!current()) return;
          if (freshCanvasNeeded) {
            const replacement = canvas.cloneNode(true);
            canvas.replaceWith(replacement);
            canvas = replacement;
            freshCanvasNeeded = false;
            state('loading', strings.loading || 'Opening the 3D studio…');
          }
          const created = await module.createProductViewer({ canvas, productId: root.dataset.productId, onReady: ready, onError: fail });
          if (!current()) { created.dispose(); freshCanvasNeeded = true; return; }
          viewer = created;
          visibility();
          if (readyMetadata || viewer.getState()?.ready) ready(readyMetadata);
        });
        await creationQueue;
      } catch { fail(); }
    }

    on(root, 'click', event => {
      const button = event.target.closest('button, a');
      if (!button || event.defaultPrevented) return;
      if (button.matches('[data-ca-studio-launch]')) launch();
      if (button.matches('[data-ca-studio-view]')) setView(button.dataset.caStudioView);
      if (button.matches('[data-ca-studio-light]') && viewer && capabilities?.lighting) {
        lighting = button.dataset.caStudioLight;
        viewer.setLighting(lighting);
        syncOptions();
      }
      if (button.matches('[data-ca-studio-door]') && viewer && capabilities?.door) {
        door = door > 0 ? 0 : 1;
        viewer.setDoor(door);
        syncOptions();
      }
      if (button.matches('[data-ca-studio-reset]') && viewer && typeof viewer.resetView === 'function') {
        viewer.resetView();
        view = viewer.getState()?.view || 'exterior';
        door = viewer.getState()?.door || 0;
        lighting = viewer.getState()?.lighting || lighting;
        syncOptions();
        canvas.focus({ preventScroll: true });
      }
      if (button.matches('[data-ca-studio-exit]')) { release(true); launcher.focus({ preventScroll: true }); }
      if (button.matches('[data-ca-studio-choose]')) {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const destination = document.getElementById(button.hash.slice(1));
        if (!destination) return;
        event.preventDefault();
        destination.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const heading = destination.querySelector('h1');
        if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
      }
    });
    on(root, 'keydown', event => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      const button = event.target.closest('[data-ca-studio-view]');
      if (!button || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const tabs = $$('[data-ca-studio-view]').filter(tab => !tab.hidden);
      const index = tabs.indexOf(button);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (['ArrowLeft','ArrowUp'].includes(event.key) ? -1 : 1) + tabs.length) % tabs.length;
      event.preventDefault();
      setView(tabs[next].dataset.caStudioView);
      tabs[next].focus({ preventScroll: true });
    });
    on(document, 'visibilitychange', visibility);
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; visibility(); }, { threshold: .01 });
      observer.observe(stage);
    }
    const purchaseObserver = new MutationObserver(syncNative);
    const summary = productRoot?.querySelector('.ca-pdp-summary');
    if (summary) purchaseObserver.observe(summary, { childList: true, subtree: true });
    const destroy = () => { release(false); life.abort(); observer?.disconnect(); purchaseObserver.disconnect(); instances.delete(root); };
    launcher.hidden = false;
    $('.ca-studio-view-tabs').hidden = false;
    syncOptions();
    syncNative();
    state('idle');
    instances.set(root, { destroy, release: owner.release });
  }

  function init(context = document) {
    instances.forEach((instance, root) => { if (!root.isConnected) instance.destroy(); });
    if (context.matches?.('[data-ca-studio]')) mount(context);
    context.querySelectorAll('[data-ca-studio]').forEach(mount);
  }
  window.CedarAmberStudio = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => instances.forEach((instance, root) => { if (event.target.contains(root)) instance.destroy(); }));
})();
